<template>
  <div class="pm-detail">
    <p v-if="!selection" class="pm-empty">
      {{ $t('partner-map.detail.empty') }}
    </p>

    <!-- ---------- partner ---------- -->
    <template v-else-if="selection.kind === 'partner'">
      <p class="pm-dt-name">
        {{ p.name }}
        <b-badge v-if="p.internal" variant="secondary">
          {{ $t('partner-map.badge.internal') }}
        </b-badge>
        <b-badge
          v-if="p.child_accounts"
          variant="light"
          :title="$t('partner-map.badge.group-title', { n: p.child_accounts })"
        >
          {{ $t('partner-map.badge.accounts', { n: p.child_accounts + 1 }) }}
        </b-badge>
        <b-badge v-if="p.is_paused" variant="warning">
          {{ $t('partner-map.badge.paused') }}
        </b-badge>
      </p>
      <p class="pm-dt-addr">
        {{
          [p.street, `${p.postal_code} ${p.city}`.trim()]
            .filter(Boolean)
            .join(', ')
        }}
        <template v-if="p.website">
          ·
          <a :href="websiteHref" target="_blank" rel="noopener">
            {{ $t('partner-map.detail.website') }}
          </a>
        </template>
      </p>

      <div class="pm-tiles">
        <div>
          <div class="l">{{ $t('partner-map.kpi.revenue', { year }) }}</div>
          <div class="v">
            {{ fmt.money(p.revenue_year) }}
            <small>
              {{
                $t('partner-map.detail.billed-jobs', {
                  n: fmt.num(p.jobs_year),
                })
              }}
            </small>
          </div>
        </div>
        <div>
          <div class="l">{{ $t('partner-map.kpi.cost', { year }) }}</div>
          <div class="v">
            {{ fmt.money(p.cost_year) }}
            <small>{{ $t('partner-map.detail.payout') }}</small>
          </div>
        </div>
        <div>
          <div class="l">{{ $t('partner-map.kpi.margin', { year }) }}</div>
          <div class="v">
            {{ fmt.money(p.margin_year) }}
            <small>{{ fmt.pct(p.margin_pct_year) }}</small>
          </div>
        </div>
        <div>
          <div class="l">{{ $t('partner-map.detail.active-jobs') }}</div>
          <div class="v">
            {{ fmt.num(p.active_jobs_now) }}
            <small>{{ $t('partner-map.detail.today') }}</small>
          </div>
        </div>
      </div>

      <div class="pm-tiles wip">
        <div>
          <div class="l">{{ $t('partner-map.kpi.revenue-wip') }}</div>
          <div class="v">
            {{ fmt.money(p.revenue_wip) }}
            <small>
              {{
                $t('partner-map.detail.open-jobs', { n: fmt.num(p.jobs_wip) })
              }}
            </small>
          </div>
        </div>
        <div>
          <div class="l">{{ $t('partner-map.kpi.cost-wip') }}</div>
          <div class="v">
            {{ fmt.money(p.cost_wip) }}
            <small>{{ $t('partner-map.detail.expected-payout') }}</small>
          </div>
        </div>
      </div>

      <dl class="pm-rows">
        <div>
          <dt>{{ $t('partner-map.detail.rating-avg') }}</dt>
          <dd>
            <RatingWidget :average="p.rating_avg" :count="p.rating_count" />
          </dd>
        </div>
        <div>
          <dt>{{ $t('partner-map.detail.rating-last') }}</dt>
          <dd><RatingWidget :average="p.rating_last" /></dd>
        </div>
        <div>
          <dt>{{ $t('partner-map.detail.completed-total') }}</dt>
          <dd>{{ fmt.num(p.completed_jobs_total) }}</dd>
        </div>
        <div>
          <dt>{{ $t('partner-map.detail.cancelled-total') }}</dt>
          <dd>{{ fmt.num(p.cancelled_jobs_total) }}</dd>
        </div>
        <div>
          <dt>{{ $t('partner-map.detail.service-hours', { year }) }}</dt>
          <dd>{{ fmt.num(Math.round(p.service_hours_year || 0)) }} h</dd>
        </div>
        <div>
          <dt>
            {{ $t('partner-map.detail.revenue-incl-products', { year }) }}
          </dt>
          <dd>{{ fmt.money(p.revenue_incl_products_year) }}</dd>
        </div>
        <div>
          <dt>{{ $t('partner-map.detail.service-radius') }}</dt>
          <dd>{{ fmt.num(p.service_area_km) }} km</dd>
        </div>
        <div>
          <dt>{{ $t('partner-map.detail.shown-jobs') }}</dt>
          <dd>{{ fmt.num(p.shown_jobs) }}</dd>
        </div>
        <div>
          <dt>{{ $t('partner-map.detail.potential-jobs') }}</dt>
          <dd>{{ fmt.num(p.potential_jobs) }}</dd>
        </div>
        <div>
          <dt>{{ $t('partner-map.detail.account-status') }}</dt>
          <dd>{{ p.account_status || '–' }}</dd>
        </div>
        <div v-if="p.child_accounts">
          <dt>{{ $t('partner-map.detail.child-accounts') }}</dt>
          <dd>
            {{ $t('partner-map.detail.consolidated', { n: p.child_accounts }) }}
          </dd>
        </div>
        <div v-if="nearestHero">
          <dt>{{ $t('partner-map.detail.nearest-hero') }}</dt>
          <dd>{{ nearestHero.ref.name }} · {{ fmt.num(nearestHero.km) }} km</dd>
        </div>
      </dl>

      <p v-if="p.jobs_all === 0" class="pm-empty text-left">
        {{ $t('partner-map.detail.no-jobs-in-filter') }}
      </p>

      <h6 class="pm-sect">
        {{ $t('partner-map.detail.by-status', { year }) }}
      </h6>
      <table class="pm-stbl">
        <thead>
          <tr>
            <th>{{ $t('partner-map.table.status') }}</th>
            <th>{{ $t('partner-map.table.jobs') }}</th>
            <th>{{ $t('partner-map.table.revenue') }}</th>
            <th>{{ $t('partner-map.table.cost') }}</th>
            <th>{{ $t('partner-map.table.margin') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in statusRows" :key="r.key" :class="{ off: !r.on }">
            <td>
              <span
                class="dot"
                :class="{ out: !r.on }"
                :style="r.on ? { background: groupColor[r.group] } : null"
              />
              {{ $t('partner-map.job-status.' + r.labelKey) }}
            </td>
            <td>{{ fmt.num(r.jobs) }}</td>
            <td>{{ fmt.money(r.rev) }}</td>
            <td>{{ fmt.money(r.cost) }}</td>
            <td>{{ fmt.money(r.rev - r.cost) }}</td>
          </tr>
          <tr class="tot">
            <td>{{ $t('partner-map.group.billed') }}</td>
            <td>{{ fmt.num(totals.billed.jobs) }}</td>
            <td>{{ fmt.money(totals.billed.rev) }}</td>
            <td>{{ fmt.money(totals.billed.cost) }}</td>
            <td>{{ fmt.money(totals.billed.rev - totals.billed.cost) }}</td>
          </tr>
          <tr v-if="totals.wip.jobs" class="tot wip">
            <td>{{ $t('partner-map.group.open') }}</td>
            <td>{{ fmt.num(totals.wip.jobs) }}</td>
            <td>{{ fmt.money(totals.wip.rev) }}</td>
            <td>{{ fmt.money(totals.wip.cost) }}</td>
            <td>{{ fmt.money(totals.wip.rev - totals.wip.cost) }}</td>
          </tr>
        </tbody>
      </table>
    </template>

    <!-- ---------- mobile hero ---------- -->
    <template v-else>
      <p class="pm-dt-name">
        {{ h.name }}
        <b-badge variant="secondary">
          {{ $t('partner-map.badge.mobile-hero') }}
        </b-badge>
      </p>
      <p class="pm-dt-addr">
        {{ h.street }} {{ h.house_number }}, {{ h.postal_code }} {{ h.city }}
      </p>

      <div class="pm-tiles">
        <div>
          <div class="l">{{ $t('partner-map.detail.util-6m') }}</div>
          <div class="v">
            {{ h.util ? fmt.pctOfOne(h.util.util) : '–' }}
            <small>
              {{
                h.util
                  ? $t('partner-map.detail.assignments', {
                      n: fmt.num(h.util.jobs),
                    })
                  : $t('partner-map.detail.no-util-data-short')
              }}
            </small>
          </div>
        </div>
        <div>
          <div class="l">{{ $t('partner-map.detail.free-capacity') }}</div>
          <div class="v">
            {{ h.util ? `${fmt.num(Math.round(h.util.free_h))} h` : '–' }}
            <small v-if="h.util">
              {{ $t('partner-map.detail.in-weeks', { n: h.util.weeks }) }}
            </small>
          </div>
        </div>
        <div>
          <div class="l">{{ $t('partner-map.detail.service-radius') }}</div>
          <div class="v">
            {{ fmt.num(h.radius_km) }} km
            <small>
              {{
                h.gid_account
                  ? $t('partner-map.detail.radius-from-account')
                  : $t('partner-map.detail.radius-default')
              }}
            </small>
          </div>
        </div>
        <div>
          <div class="l">{{ $t('partner-map.detail.nearest-partner') }}</div>
          <div class="v">
            <template v-if="nearestPartner">
              {{ fmt.num(nearestPartner.km) }} km
              <small>{{ nearestPartner.ref.name }}</small>
            </template>
            <template v-else>–</template>
          </div>
        </div>
      </div>

      <template v-if="h.util">
        <h6 class="pm-sect">
          {{
            $t('partner-map.detail.util-by-month', { window: utilWindowLabel })
          }}
        </h6>
        <div class="pm-spark">
          <span
            class="sp-100"
            :title="$t('partner-map.detail.net-capacity-100')"
          />
          <span
            v-for="m in months"
            :key="m.key"
            class="sp-col"
            :title="m.title"
          >
            <span class="sp-bar">
              <i
                :style="{ height: m.height + '%' }"
                :class="{ over: m.value > 1 }"
              />
            </span>
            <span class="sp-lbl">{{ m.label }}</span>
            <span class="sp-val">
              {{ m.value == null ? '–' : Math.round(m.value * 100) }}
            </span>
          </span>
        </div>
        <dl class="pm-rows">
          <div>
            <dt>{{ $t('partner-map.detail.booked-time') }}</dt>
            <dd>{{ fmt.num(Math.round(h.util.booked_h)) }} h</dd>
          </div>
          <div>
            <dt>{{ $t('partner-map.detail.booked-nodrive') }}</dt>
            <dd>
              {{ fmt.num(Math.round(h.util.booked_nodrive_h)) }} h ·
              {{ fmt.pctOfOne(h.util.util_nodrive) }}
            </dd>
          </div>
          <div>
            <dt>{{ $t('partner-map.detail.net-capacity') }}</dt>
            <dd>{{ fmt.num(Math.round(h.util.net_cap_h)) }} h</dd>
          </div>
          <div>
            <dt>{{ $t('partner-map.detail.absence') }}</dt>
            <dd>{{ fmt.num(Math.round(h.util.absent_h)) }} h</dd>
          </div>
          <div>
            <dt>{{ $t('partner-map.detail.observed-weeks') }}</dt>
            <dd>
              {{ h.util.weeks }} ({{ h.util.first_week }} –
              {{ h.util.last_week }})
            </dd>
          </div>
        </dl>
      </template>
      <p v-else class="pm-empty text-left">
        {{ $t('partner-map.detail.no-util-data') }}
      </p>

      <h6 class="pm-sect">{{ $t('partner-map.detail.linkage') }}</h6>
      <dl class="pm-rows">
        <div>
          <dt>{{ $t('partner-map.detail.gid-account') }}</dt>
          <dd>{{ h.gid_account || $t('partner-map.detail.not-linked') }}</dd>
        </div>
        <div>
          <dt>{{ $t('partner-map.detail.account-status') }}</dt>
          <dd>{{ h.gid_account_status || '–' }}</dd>
        </div>
        <div>
          <dt>{{ $t('partner-map.detail.geocoding') }}</dt>
          <dd>
            {{
              h.geo_source === 'gid-account'
                ? $t('partner-map.detail.geo-account')
                : $t('partner-map.detail.geo-osm')
            }}
          </dd>
        </div>
      </dl>
    </template>
  </div>
</template>

<script>
import RatingWidget from '@/views/partners/RatingWidget.vue';
import {
  GROUP_COLOR,
  STATUS_META,
  isBilled,
  nearest,
  statusGroup,
} from './model';

export default {
  name: 'PartnerMapDetail',
  components: { RatingWidget },
  props: {
    selection: { type: Object, default: null },
    /** Selected status keys — drives which rows count towards the totals. */
    activeStatuses: { type: Set, required: true },
    partners: { type: Array, required: true },
    heroes: { type: Array, required: true },
    year: { type: Number, required: true },
    utilWindow: { type: Object, default: null },
    fmt: { type: Object, required: true },
  },
  data() {
    return { groupColor: GROUP_COLOR };
  },
  computed: {
    p() {
      return this.selection && this.selection.kind === 'partner'
        ? this.selection.ref
        : null;
    },
    h() {
      return this.selection && this.selection.kind === 'hero'
        ? this.selection.ref
        : null;
    },
    websiteHref() {
      const w = (this.p && this.p.website) || '';
      return w.startsWith('http') ? w : `https://${w}`;
    },
    nearestHero() {
      return this.p ? nearest(this.p, this.heroes) : null;
    },
    nearestPartner() {
      return this.h ? nearest(this.h, this.partners) : null;
    },
    /** Every status the partner has jobs in — active ones first, then by revenue. */
    statusRows() {
      if (!this.p) return [];
      return Object.keys(this.p.by_status || {})
        .map((k) => {
          const v = this.p.by_status[k];
          return {
            key: k,
            labelKey: (STATUS_META[k] || [k])[0],
            group: statusGroup(k),
            jobs: v[0],
            rev: v[1],
            cost: v[2],
            on: this.activeStatuses.has(k),
          };
        })
        .sort((a, b) => b.on - a.on || b.rev - a.rev);
    },
    totals() {
      const t = {
        billed: { jobs: 0, rev: 0, cost: 0 },
        wip: { jobs: 0, rev: 0, cost: 0 },
      };
      this.statusRows.forEach((r) => {
        if (!r.on) return;
        const bucket = isBilled(r.key) ? t.billed : t.wip;
        bucket.jobs += r.jobs;
        bucket.rev += r.rev;
        bucket.cost += r.cost;
      });
      return t;
    },
    utilWindowLabel() {
      if (!this.utilWindow) return '';
      const d = (s) => `${s.slice(8, 10)}.${s.slice(5, 7)}.`;
      return `${d(this.utilWindow.from)} – ${d(
        this.utilWindow.to,
      )}${this.utilWindow.to.slice(0, 4)}`;
    },
    months() {
      if (!this.h || !this.h.util) return [];
      const src = this.h.util.months || {};
      return Object.keys(src)
        .sort()
        .map((key) => {
          // [booked h, capacity h, assignments, utilization]
          const m = src[key];
          const value = m[3];
          const label = this.$t(
            `partner-map.month.${parseInt(key.slice(5, 7), 10)}`,
          );
          return {
            key,
            label,
            value,
            // The bar scale tops out at 125 % so an overloaded month stays on the chart.
            height: value == null ? 0 : (Math.min(1.25, value) / 1.25) * 100,
            title: `${label} ${key.slice(0, 4)}: ${this.fmt.pctOfOne(
              value,
            )} — ${this.fmt.num(Math.round(m[0]))} h / ${this.fmt.num(
              Math.round(m[1]),
            )} h, ${this.fmt.num(m[2])}`,
          };
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.pm-detail {
  font-size: 13px;
}

.pm-empty {
  color: var(--pm-ink-3);
  text-align: center;
  padding: 18px 4px;
  margin: 0;
}

.pm-dt-name {
  font-size: 15px;
  font-weight: 700;
  margin: 0 0 2px;
}

.pm-dt-addr {
  color: var(--pm-ink-2);
  margin: 0 0 12px;
}

.pm-sect {
  margin: 16px 0 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--pm-ink-3);
}

.pm-tiles {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 8px;

  > div {
    background: var(--pm-surface-2);
    border: 1px solid var(--pm-border);
    border-radius: 5px;
    padding: 7px 9px;
  }
  .l {
    font-size: 11px;
    color: var(--pm-ink-3);
  }
  .v {
    font-size: 15px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }
  .v small {
    display: block;
    font-size: 11px;
    font-weight: 400;
    color: var(--pm-ink-3);
  }
  &.wip > div {
    // Backlog, not billed — visually separated so the two can never be added up by eye.
    background: transparent;
    border-style: dashed;
  }
}

.pm-rows {
  margin: 10px 0 0;

  > div {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 3px 0;
    border-bottom: 1px solid var(--pm-border);
  }
  dt {
    font-weight: 400;
    color: var(--pm-ink-2);
  }
  dd {
    margin: 0;
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
}

.pm-stbl {
  width: 100%;
  font-size: 12px;
  font-variant-numeric: tabular-nums;

  th {
    font-weight: 600;
    color: var(--pm-ink-3);
    border-bottom: 1px solid var(--pm-border);
    padding: 3px 0;
  }
  th + th,
  td + td {
    text-align: right;
    padding-left: 8px;
  }
  td {
    padding: 3px 0;
    border-bottom: 1px solid var(--pm-border);
  }
  tr.off {
    color: var(--pm-ink-3);
  }
  tr.tot td {
    font-weight: 700;
    border-top: 1.5px solid var(--pm-ink-3);
    border-bottom: 0;
  }
  tr.tot.wip td {
    font-weight: 400;
    border-top: 0;
    color: var(--pm-ink-2);
  }
  .dot {
    display: inline-block;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    margin-right: 5px;
  }
  .dot.out {
    border: 1px solid var(--pm-ink-3);
  }
}

.pm-spark {
  position: relative;
  display: flex;
  align-items: flex-end;
  gap: 4px;
  padding-top: 4px;

  .sp-100 {
    // The 100 % reference line sits where a full bar would end (100/125 of the height).
    position: absolute;
    left: 0;
    right: 0;
    top: calc(4px + 20% * 0.9);
    border-top: 1px dashed var(--pm-ink-3);
  }
  .sp-col {
    flex: 1 1 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1px;
  }
  .sp-bar {
    width: 100%;
    height: 54px;
    display: flex;
    align-items: flex-end;
    background: var(--pm-surface-2);
    border-radius: 2px;
  }
  .sp-bar i {
    width: 100%;
    background: var(--pm-hero);
    border-radius: 2px;
  }
  .sp-bar i.over {
    background: var(--pm-crit);
  }
  .sp-lbl {
    font-size: 9.5px;
    color: var(--pm-ink-3);
  }
  .sp-val {
    font-size: 10px;
    font-variant-numeric: tabular-nums;
    color: var(--pm-ink-2);
  }
}
</style>
